import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
} from '@/components/ui'
import { FileUpload } from '@/components/upload'
import {
  ArrowRight,
  CheckCircle,
  Play,
  Loader2,
  Eye,
  RefreshCw,
  AlertCircle,
} from 'lucide-react'
import { jobsApi, uploadApi } from '@/services/api'

interface UploadedFile {
  file: File
  id: string
  type: 'brcs' | 'brig' | 'amazon' | 'perfios' | 'unknown'
  status: 'pending' | 'uploading' | 'success' | 'error'
  error?: string
}

// Job result type
interface JobResult {
  memo_id?: string
  risk_score?: number
  [key: string]: unknown
}

type AnalysisStatus = 'idle' | 'running' | 'completed' | 'failed'

export function Upload() {
  const navigate = useNavigate()
  const [customerId, setCustomerId] = useState('')
  const [files, setFiles] = useState<UploadedFile[]>([])
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1)
  const [analysisStatus, setAnalysisStatus] = useState<AnalysisStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [jobResult, setJobResult] = useState<JobResult | null>(null)
  const [taskType, setTaskType] = useState<'amazon' | 'amazon_growth'>('amazon')

  const taskTypeLabel = taskType === 'amazon_growth' ? 'Amazon_growth' : 'Amazon_risk'

  useEffect(() => {
    // no-op (kept to avoid changing component structure)
  }, [])

  const handleFilesSelected = (selectedFiles: UploadedFile[]) => {
    setFiles(selectedFiles)
  }

  const handleContinue = () => {
    if (step === 1 && customerId) {
      setStep(2)
    } else if (step === 2 && files.length > 0) {
      setStep(3)
    } else if (step === 3) {
      // Move to analysis step
      setStep(4)
    }
  }

  const runAnalysis = async () => {
    setAnalysisStatus('running')
    setError(null)
    const uniqueCustomerId = customerId + Date.now()
    setCustomerId(uniqueCustomerId)

    try {
      // Step 1: Upload all files to the backend first
      console.log('Uploading files to backend...')
      const uploadedFilePaths: string[] = []
      const uploadedFileUrls: string[] = []
      
      for (const f of files) {
        try {
          const result = await uploadApi.uploadFile(
            f.file,
            uniqueCustomerId,
            f.type !== 'unknown' ? f.type : undefined,
            true  // Save directly to customers/ directory
          )
          uploadedFilePaths.push(result.path)
          if (result.url) {
            uploadedFileUrls.push(result.url)
          }
          console.log(`Uploaded: ${f.file.name} -> ${result.path}`)
        } catch (uploadErr) {
          console.error(`Failed to upload ${f.file.name}:`, uploadErr)
          throw new Error(`Failed to upload file: ${f.file.name}`)
        }

      }

      // Step 2: Determine job type based on uploaded files
      const fileTypes = files.map(f => f.type)
      if (fileTypes.every(t => t === 'amazon')) {
        // ok
      } else if (fileTypes.every(t => t === 'brcs')) {
        // ok
      } else if (fileTypes.every(t => t === 'brig')) {
        // ok
      }

      // Step 3: Create job via API (files are now on the server)
      const job = await jobsApi.create({
        type: taskType,
        customerId: uniqueCustomerId,
        inputFiles: uploadedFilePaths.length > 0 ? uploadedFilePaths : files.map(f => f.file.name),
        inputFileUrls: uploadedFileUrls,
      })

      // Poll for real job status
      const pollInterval = setInterval(async () => {
        try {
          const updatedJob = await jobsApi.get(job.id)
          
          if (updatedJob.status === 'completed') {
            clearInterval(pollInterval)
            const result = updatedJob.result as unknown as JobResult
            setJobResult(result)
            setAnalysisStatus('completed')
            
            // Navigate to memo viewer if memo was generated
            const memoId = result?.memo_id
            if (memoId) {
              if (taskType === 'amazon_growth') {
                navigate(`/growth-memo/${memoId}`)
              } else {
                navigate(`/memo/${memoId}`)
              }
            }
          } else if (updatedJob.status === 'failed') {
            clearInterval(pollInterval)
            setAnalysisStatus('failed')
            setError(updatedJob.error || 'Analysis failed')
          }
        } catch (err) {
          console.error('Error polling job status:', err)
          // Don't auto-complete on error - just log and keep trying
        }
      }, 2000) // Poll every 2 seconds

    } catch (err) {
      console.error('Failed to start analysis:', err)
      setAnalysisStatus('failed')
      // Show more detailed error
      const errorMessage = err instanceof Error ? err.message : 
        (typeof err === 'object' && err !== null && 'response' in err) 
          ? (err as any).response?.data?.detail || 'Failed to start analysis'
          : 'Failed to start analysis. Please try again.'
      setError(errorMessage)
    }
  }

  const startNew = () => {
    setStep(1)
    setCustomerId('')
    setFiles([])
    setAnalysisStatus('idle')
    setError(null)
    setJobResult(null)
    setTaskType('amazon')
  }

  return (
    <div className={`space-y-6 ${analysisStatus === 'completed' ? 'w-full' : 'max-w-4xl mx-auto'}`}>
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {step === 4 ? 'Credit Analysis' : 'Upload Customer Data'}
          </h1>
          <p className="text-muted-foreground">
            {step === 4 
              ? 'Run analysis and view results'
              : 'Upload data files to run credit analysis'}
          </p>
        </div>
        {analysisStatus === 'completed' && (
          <Button onClick={startNew} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            New Analysis
          </Button>
        )}
      </div>

      {/* Progress Steps */}
      <div className="flex items-center gap-4">
        {[
          { num: 1, label: 'Customer ID' },
          { num: 2, label: 'Upload Files' },
          { num: 3, label: 'Review' },
          { num: 4, label: 'Analysis' },
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s.num < step
                  ? 'bg-risk-excellent text-white'
                  : s.num === step
                  ? 'bg-dashboard-navy text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {s.num < step ? <CheckCircle className="h-5 w-5" /> : s.num}
            </div>
            <span className={`text-sm ${s.num === step ? 'font-medium' : 'text-gray-500'}`}>
              {s.label}
            </span>
            {idx < 3 && <div className="w-8 h-0.5 bg-gray-200" />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Start a New Task</CardTitle>
            <CardDescription>
              Choose task type and enter a customer ID
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Task Type</label>

              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="text-sm font-bold text-gray-800">Risk type</div>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setTaskType('amazon')}
                      className={`p-3 text-left border rounded-lg transition-all hover:border-dashboard-teal ${
                        taskType === 'amazon'
                          ? 'border-dashboard-teal bg-dashboard-teal/5 ring-1 ring-dashboard-teal'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-sm">Amazon_risk</div>
                      <div className="text-xs text-muted-foreground mt-1">Credit memo / risk report</div>
                    </button>

                    <button
                      disabled
                      className="p-3 text-left border rounded-lg border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                    >
                      <div className="font-medium text-sm">Shopify_risk</div>
                      <div className="text-xs mt-1">Coming soon</div>
                    </button>

                    <button
                      disabled
                      className="p-3 text-left border rounded-lg border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                    >
                      <div className="font-medium text-sm">Walmart_risk</div>
                      <div className="text-xs mt-1">Coming soon</div>
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-bold text-gray-800">Growth insight</div>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setTaskType('amazon_growth')}
                      className={`p-3 text-left border rounded-lg transition-all hover:border-dashboard-teal ${
                        taskType === 'amazon_growth'
                          ? 'border-dashboard-teal bg-dashboard-teal/5 ring-1 ring-dashboard-teal'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-sm">Amazon_growth</div>
                      <div className="text-xs text-muted-foreground mt-1">Insights / recommendations</div>
                    </button>

                    <button
                      disabled
                      className="p-3 text-left border rounded-lg border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                    >
                      <div className="font-medium text-sm">Shopify_growth</div>
                      <div className="text-xs mt-1">Coming soon</div>
                    </button>

                    <button
                      disabled
                      className="p-3 text-left border rounded-lg border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed"
                    >
                      <div className="font-medium text-sm">Walmart_growth</div>
                      <div className="text-xs mt-1">Coming soon</div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t">
              <label className="text-sm font-medium text-gray-700">Customer ID</label>
              <Input
                placeholder="e.g., Customer-1, customer4, CUST001"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customerId) {
                    handleContinue()
                  }
                }}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use any format.
              </p>
            </div>
            <Button
              onClick={handleContinue}
              disabled={!customerId}
              className="w-full gap-2"
            >
              Continue <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && files.length > 0) {
              handleContinue()
            }
          }}
        >
          <CardHeader>
            <CardTitle>Upload Data Files</CardTitle>
            <CardDescription>
              Upload BRCS, BRIG, Amazon, or Perfios data files for{' '}
              <Badge variant="secondary">{customerId}</Badge>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FileUpload onFilesSelected={handleFilesSelected} />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>
                Back
              </Button>
              <Button
                onClick={handleContinue}
                disabled={files.length === 0}
                className="flex-1 gap-2"
              >
                Continue <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleContinue()
            }
          }}
        >
          <CardHeader>
            <CardTitle>Review & Submit</CardTitle>
            <CardDescription>
              Review your upload configuration before running analysis
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Task Type</span>
                <span className="text-sm font-medium">{taskTypeLabel}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Customer ID</span>
                <span className="text-sm font-medium">{customerId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Files</span>
                <span className="text-sm font-medium">{files.length} files</span>
              </div>
              <div className="border-t pt-3">
                <span className="text-sm text-gray-600">File Types Detected:</span>
                <div className="flex gap-2 mt-2 flex-wrap">
                  {Array.from(new Set(files.map((f) => f.type))).map((type) => (
                    <Badge key={type} variant="secondary">
                      {type.toUpperCase()}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="border-t pt-3">
                <span className="text-sm text-gray-600">Files to process:</span>
                <ul className="mt-2 space-y-1">
                  {files.map(f => (
                    <li key={f.id} className="text-sm flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {f.type.toUpperCase()}
                      </Badge>
                      {f.file.name}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(2)}>
                Back
              </Button>
              <Button onClick={handleContinue} className="flex-1 gap-2">
                Continue to Analysis <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Run Analysis */}
      {step === 4 && (
        <div className="space-y-6">
          {/* Analysis Controls */}
          {analysisStatus === 'idle' && (
            <Card
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  runAnalysis()
                }
              }}
            >
              <CardHeader>
                <CardTitle className="text-lg">Ready to Analyze</CardTitle>
                <CardDescription>
                  Click the button below to start credit analysis for <Badge variant="secondary">{customerId}</Badge>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium">{files.length} files ready</p>
                      <p className="text-xs text-muted-foreground">
                        Types: {Array.from(new Set(files.map(f => f.type))).join(', ').toUpperCase()}
                      </p>
                    </div>
                    <Button onClick={runAnalysis} className="gap-2">
                      <Play className="h-4 w-4" />
                      Run Analysis
                    </Button>
                  </div>
                </div>
                <Button variant="outline" onClick={() => setStep(3)} className="w-full">
                  Back to Review
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Running State */}
          {analysisStatus === 'running' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Analysis in Progress</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="h-10 w-10 rounded-full border-4 border-dashboard-teal/20"></div>
                      <div className="absolute top-0 left-0 h-10 w-10 rounded-full border-4 border-transparent border-t-dashboard-teal animate-spin"></div>
                    </div>
                    <div>
                      <span className="font-medium">Processing analysis...</span>
                      <p className="text-sm text-muted-foreground">
                        Agents are analyzing customer data. This may take a moment.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Generating credit profile for {customerId}...</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Failed State */}
          {analysisStatus === 'failed' && (
            <Card
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  runAnalysis()
                }
              }}
            >
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-red-500" />
                  Analysis Failed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-red-600">{error || 'An unknown error occurred'}</p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setStep(3)}>
                      Back to Review
                    </Button>
                    <Button onClick={runAnalysis} className="gap-2">
                      <RefreshCw className="h-4 w-4" />
                      Retry Analysis
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Completed State - Show Results */}
          {analysisStatus === 'completed' && (
            <>
              {/* Success Header */}
              <Card className="border-green-200 bg-green-50">
                <CardContent className="py-6">
                  <div className="flex flex-col items-center justify-center gap-4">
                    <CheckCircle className="h-12 w-12 text-green-600" />
                    <div className="text-center">
                      <p className="text-lg font-medium text-green-800">Analysis Complete</p>
                      <p className="text-sm text-green-600">
                        Credit memo generated for {customerId}
                      </p>
                      {jobResult?.risk_score !== undefined && (
                        <p className="text-sm text-green-600 mt-1">
                          Risk Score: {jobResult.risk_score}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 mt-2">
                      {jobResult?.memo_id ? (
                        <Button
                          className="gap-2"
                          onClick={() =>
                            navigate(
                              taskType === 'amazon_growth'
                                ? `/growth-memo/${jobResult.memo_id}`
                                : `/memo/${jobResult.memo_id}`
                            )
                          }
                        >
                          <Eye className="h-4 w-4" />
                          View Memo
                        </Button>
                      ) : (
                        <Button variant="outline" className="gap-2" onClick={() => navigate('/reports')}>
                          <Eye className="h-4 w-4" />
                          View in Reports
                        </Button>
                      )}
                      <Button variant="outline" onClick={startNew} className="gap-2">
                        <RefreshCw className="h-4 w-4" />
                        New Analysis
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}
    </div>
  )
}
